import type { FC } from "react";
import { useCallback, useMemo } from "react";

import { StrengthIndicator } from "@ui/components/StrengthIndicator";
import { useAddProduct } from "@ui/hooks/useAddProduct";
import { getOptimizedMagentoImageSrcSet } from "@ui/utils/getOptimizedMagentoImageSrcSet";
import { isValidPrice } from "@ui/utils/isValidPrice/isValidPrice";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { type DirectionalIconComponent } from "@jti/brand-icons";
import { Badge } from "@jti/core-components/components/Badge";
import { Button } from "@jti/core-components/components/Button";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import type { ProductNicotineStrength } from "@jti/magento";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { FormattedPrice } from "../FormattedPrice";
import { ProductTryNowButton } from "../ProductTryNowButton";

import { LeftBadge } from "./components/LeftBadge";
import { ProductCardColors } from "./components/ProductCardColors/ProductCardColors";
import { buildProductCardSelectItemData } from "./utils/buildProductCardSelectItemData";

import styles from "./ProductCard.module.scss";

const { sanitize } = dompurify;

export type ProductCardProps = {
  title: string;
  description?: string;
  sanitizedDescription?: string;
  image: {
    src?: string;
    alt: string;
  };
  badge?: {
    left?: string;
    right?: string;
    leftIcon?: string;
    rightIcon?: string;
  };
  primaryButton?: {
    href?: string;
    text?: string;
    enabled: boolean;
  };
  secondaryButton?: {
    href?: string;
    text?: string;
  };
  oldPrice?: number;
  currentPrice?: number;
  currency?: string;
  discount?: number;
  colors?: string[];
  colorLabels?: string[];
  additionalInfo?: {
    text: string;
    icon?: DirectionalIconComponent;
  };
  taxInfo?: string;
  isLoading?: boolean;
  dataSku?: string;
  isFreeTrialProduct?: boolean;
  isBundle?: boolean;
  imageFetchPriority?: "auto" | "high" | "low";
  categoryId?: string;
  categoryName?: string;
  shouldShowQuickAddToCart?: boolean;
  nicotineStrength?: ProductNicotineStrength;
};

export const ProductCard: FC<ProductCardProps> = ({
  title,
  description,
  sanitizedDescription,
  image,
  badge,
  oldPrice,
  currentPrice,
  discount,
  currency,
  primaryButton,
  secondaryButton,
  colors,
  colorLabels,
  additionalInfo,
  isLoading,
  dataSku,
  taxInfo,
  isFreeTrialProduct,
  isBundle = false,
  imageFetchPriority = "auto",
  categoryId,
  categoryName,
  shouldShowQuickAddToCart = false,
  nicotineStrength,
}) => {
  const { isRunning, addProductToCart } = useAddProduct(dataSku || "", [], false);

  const showPrimaryButton = primaryButton?.enabled && primaryButton?.href && primaryButton?.text && !isFreeTrialProduct;

  const isPriorityImage = imageFetchPriority === "high";

  const isCurrentPriceValid = isValidPrice(currentPrice);
  const isOldPriceValid = isValidPrice(oldPrice);
  const { optimizedImageSrcSet, optimizedImageSrc1x } = getOptimizedMagentoImageSrcSet({
    src: image.src,
    baseWidth: 278,
    baseHeight: 185,
  });
  const descriptionHtml = sanitizedDescription ?? (description ? sanitize(description) : "");

  const handleBuyNowClick = useCallback(() => {
    if (!dataSku && !title) {
      return;
    }

    const selectItemData = buildProductCardSelectItemData({
      title,
      dataSku,
      currentPrice,
      currency,
      discount,
      colors,
      colorLabels,
      primaryButtonText: primaryButton?.text,
      isBundle,
      categoryName,
    });

    dispatchTrackingEvent(TrackingEvents.TrackingSelectItem, selectItemData);
  }, [
    title,
    dataSku,
    currentPrice,
    currency,
    discount,
    colors,
    colorLabels,
    primaryButton?.text,
    isBundle,
    categoryName,
  ]);

  const handleQuickAddClick = useCallback(() => {
    if (shouldShowQuickAddToCart) {
      addProductToCart(1);
    }

    handleBuyNowClick();
  }, [shouldShowQuickAddToCart, addProductToCart, handleBuyNowClick]);

  const primaryButtonConfig = useMemo(() => {
    if (!showPrimaryButton) return null;

    return shouldShowQuickAddToCart
      ? {
          onClick: handleQuickAddClick,
          pending: isRunning,
        }
      : {
          href: primaryButton.href,
          onClick: handleBuyNowClick,
        };
  }, [
    showPrimaryButton,
    shouldShowQuickAddToCart,
    handleQuickAddClick,
    isRunning,
    primaryButton?.href,
    handleBuyNowClick,
  ]);

  const cardOverlayLink = useMemo(() => {
    if (!isFreeTrialProduct && primaryButton?.href) {
      return { href: primaryButton.href, text: primaryButton.text };
    }

    if (secondaryButton?.href) {
      return { href: secondaryButton.href, text: secondaryButton.text };
    }

    return null;
  }, [isFreeTrialProduct, primaryButton, secondaryButton]);

  const handleSecondaryClick = useCallback(() => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: secondaryButton?.text ?? "",
      cta_location: "product-card",
    });
  }, [secondaryButton?.text]);

  return (
    <div
      className={classNames(styles.card, { [styles.cardLoading]: isLoading })}
      data-testid="product-card"
      data-sku={dataSku}
    >
      <div className={styles.topSection}>
        <div className={styles.badgeSection}>
          <LeftBadge
            isFreeTrialProduct={isFreeTrialProduct}
            label={badge?.left}
            customIcon={badge?.leftIcon ? { src: badge.leftIcon } : undefined}
          />
          {badge?.right ? (
            <Badge label={badge.right} customIcon={badge?.rightIcon ? { src: badge.rightIcon } : undefined} />
          ) : (
            <span />
          )}
        </div>
        <img
          src={optimizedImageSrc1x}
          srcSet={optimizedImageSrcSet}
          className={styles.productImage}
          alt={image.alt}
          fetchPriority={imageFetchPriority}
          loading={isPriorityImage ? "eager" : "lazy"}
          decoding="async"
        />
        <div className={styles.textSection}>
          {title && <h2 className={styles.title}>{title}</h2>}
          {descriptionHtml && (
            <div
              className={styles.description}
              // eslint-disable-next-line jam3/no-sanitizer-with-danger -- This is either server-sanitized or sanitized above.
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            ></div>
          )}

          {nicotineStrength && (
            <StrengthIndicator
              name={nicotineStrength.name}
              strength={nicotineStrength.rating}
              className={styles.strength}
            />
          )}

          {isCurrentPriceValid ? (
            <div className={styles.price}>
              {isOldPriceValid && oldPrice !== currentPrice && (
                <s className={styles.discount}>
                  <FormattedPrice value={oldPrice} currency={currency ?? ""} />
                </s>
              )}
              <FormattedPrice value={currentPrice} currency={currency ?? ""} />
            </div>
          ) : null}

          {taxInfo && <div className={styles.taxInfo}>{taxInfo}</div>}

          {additionalInfo && (
            <div className={styles.additionalInfoSection}>
              {additionalInfo.icon && <additionalInfo.icon className={styles.additionalInfoSectionIcon} />}
              <div className={styles.additionalInfoSectionText}>{additionalInfo.text}</div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.bottomSection}>
        <div data-testid="buttons" className={styles.buttonSection}>
          {primaryButtonConfig && (
            <Button variant="primary" size="sm" {...primaryButtonConfig}>
              {primaryButton?.text ?? ""}
            </Button>
          )}
          {isFreeTrialProduct && (
            <ProductTryNowButton
              sku={dataSku ?? ""}
              label={primaryButton?.text ?? ""}
              isBundle={isBundle}
              productPageUrl={primaryButton?.href ?? ""}
              size="sm"
              stretch={true}
              categoryId={categoryId}
            />
          )}
          {secondaryButton?.href && secondaryButton?.text && (
            <Button variant="secondary" size="sm" href={secondaryButton.href} onClick={handleSecondaryClick}>
              {secondaryButton.text}
            </Button>
          )}
        </div>
        <div className={styles.footer}>
          <ProductCardColors colors={colors} />
          {discount ? (
            <div className={styles.discountRibbon}>
              <Badge variant="club-tier3" label={`-${discount}%`} />
            </div>
          ) : null}
        </div>
      </div>
      {cardOverlayLink && (
        <LinkBehaviour
          aria-label={cardOverlayLink.text}
          data-testid="productCardLinkOverlay"
          href={cardOverlayLink.href}
          title={cardOverlayLink.text}
          className={styles.cardLinkOverlay}
          tabIndex={-1}
        />
      )}
    </div>
  );
};
